import { Container, Text, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { BoardPanel } from '../board/BoardPanel';
import { animate, COLOURS, uiStyle } from '../board/ui';
import { NoticeStack, type NoticeHandle } from './NoticeStack';
import { OverlayStage, type OverlayHandle, type OverlayTapZone } from './OverlayStage';

/** How long a banner stays up unless it's tapped away. */
const LIFE_MS = 5000;
const FLOOR = 'shared:board:floor';

/** The banners on screen as plain data, for browser tests. */
export interface SyncBannerSnapshot {
  banners: string[];
}

interface Banner {
  root: Container;
  board: BoardPanel;
  text: Text;
  zone: OverlayTapZone;
  slot: NoticeHandle | null;
  h: number;
  /** Its place in the notices' column. */
  y: number;
  shown: boolean;
  gone: boolean;
}

/**
 * When something the player did couldn't reach the server (their unlock,
 * a wallet sync, a claim), a calm line says so under the top bar
 * ("Couldn't sync your gems. They'll catch up next time"): what they did
 * still counts on this device. On a small board (the reel floor with a
 * brass edge); it goes after a few seconds, or when tapped. Each takes its
 * place in the game's column of notices ({@link NoticeStack}), under any
 * already showing, so none overlap. Drawn on the overlay canvas over any
 * screen.
 */
export class SyncBannerLayer extends Container {
  private banners: Banner[] = [];
  private w = 1;

  constructor(private readonly floor: () => Texture | null) {
    super();
    this.eventMode = 'none';
  }

  layout(width: number): void {
    this.w = width;
    this.arrange();
  }

  get count(): number {
    return this.banners.length;
  }

  get snapshot(): SyncBannerSnapshot {
    return { banners: this.banners.filter((b) => !b.gone).map((b) => b.text.text) };
  }

  /** Show `line`; resolves when it has gone (timed out or tapped). */
  show(line: string): Promise<void> {
    const width = this.bannerWidth();
    const text = new Text({ text: line, style: { ...uiStyle(14, COLOURS.cream, '600'), align: 'center', wordWrap: true, wordWrapWidth: width - 28, lineHeight: 18 } });
    text.anchor.set(0.5, 0);
    const h = Math.round(text.height + 18);
    const board = new BoardPanel({ floor: this.floor(), radius: 12 });
    board.resize(width, h);
    const root = new Container();
    root.addChild(board, text);
    text.position.set(width / 2, 9);
    this.addChild(root);
    return new Promise((resolve) => {
      const banner: Banner = {
        root,
        board,
        text,
        h,
        y: 0,
        shown: true,
        slot: null,
        gone: false,
        zone: OverlayStage.shared.tapZone(`${line}. Tap to dismiss`, () => void dismiss()),
      };
      const dismiss = async (): Promise<void> => {
        if (banner.gone) return;
        banner.gone = true;
        clearTimeout(timer);
        banner.zone.remove();
        await animate(220, (t) => (root.alpha = 1 - t));
        banner.slot?.release();
        this.banners = this.banners.filter((b) => b !== banner);
        if (!root.destroyed) root.destroy({ children: true });
        resolve();
      };
      const timer = setTimeout(() => void dismiss(), LIFE_MS);
      this.banners.push(banner);
      root.alpha = 0;
      banner.slot = NoticeStack.shared.add(
        h,
        ({ y, shown }) => {
          banner.y = y;
          banner.shown = shown;
          this.place(banner);
        },
        () => (banner.gone || !banner.shown || root.destroyed ? null : { x: root.x, y: root.y, width: board.boardWidth, height: h }),
      );
      void animate(260, (t) => (root.alpha = t));
    });
  }

  private arrange(): void {
    for (const banner of this.banners) this.place(banner);
  }

  /** At its place in the column, centred; its tap spot with it. */
  private place(banner: Banner): void {
    if (banner.gone || banner.root.destroyed) return;
    const width = this.bannerWidth();
    const x = Math.round((this.w - width) / 2);
    if (banner.board.boardWidth !== width) {
      banner.board.resize(width, banner.h);
      banner.text.style.wordWrapWidth = width - 28;
      banner.text.x = width / 2;
    }
    banner.root.position.set(x, banner.y);
    banner.root.visible = banner.shown;
    banner.zone.place(banner.shown ? { x, y: banner.y, width, height: banner.h } : { x: 0, y: 0, width: 0, height: 0 });
  }

  private bannerWidth(): number {
    return Math.round(Math.min(420, Math.max(220, this.w - 32)));
  }
}

declare global {
  interface Window {
    /** The connection banners on screen, for browser tests. */
    __syncBanner?: { ui: () => SyncBannerSnapshot };
  }
}

let layer: SyncBannerLayer | null = null;
let handle: OverlayHandle | null = null;
let pending = 0;

/**
 * Say over whatever is on screen (the overlay canvas) that something
 * didn't reach the server. `floor` is the board's floor art by URL.
 */
export async function showSyncBanner(line: string, art: { floor: string }): Promise<void> {
  pending += 1;
  try {
    await AssetLoader.shared.loadTextures([{ alias: FLOOR, src: art.floor }]).catch(() => undefined);
    if (!layer || !handle) {
      const piece = new SyncBannerLayer(() => AssetLoader.shared.getTexture(FLOOR));
      layer = piece;
      handle = OverlayStage.shared.show(piece, { layer: 40 });
      window.__syncBanner = { ui: () => piece.snapshot };
    }
    const current = layer;
    if (await handle.shown) await current.show(line);
  } finally {
    pending -= 1;
    if (pending === 0 && layer) {
      handle?.remove();
      layer.destroy({ children: true });
      layer = null;
      handle = null;
      delete window.__syncBanner;
    }
  }
}
